<template>
  <validation-provider
    :immediate="immediate"
    :rules="rules"
    :name="name"
    :vid="name"
    v-slot="validation"
    tag="div"
  >
    <div
      :class="{
        [$style.container]: true,
        [$style.hasContent]: hasContent,
        [$style.disabled]: disabled,
        [$style.invalid]: validation.valid === false,
        [$style.valid]: validation.valid === true
      }"
    >
      <label :class="$style.label" :for="idFromName">{{ label }}</label>
      <div :class="$style.inputWrapper">
        <!-- :key required to force update when changing selected value programatically -->
        <!-- @keypress.enter.native.prevent="" required to disable form submit when selecting an option pressing Enter key -->
        <v-select
          @keypress.enter.native.prevent=""
          v-model="innerValue"
          :disabled="disabled"
          :name="name"
          :inputId="idFromName"
          :key="value"
          :options="options"
          :clearable="false"
          :searchable="searchable"
          :components="components"
          :getOptionLabel="getOptionLabel"
          :reduce="reduce"
        />
      </div>
      <div :class="$style.error">{{ validation.errors[0] }}</div>
    </div>
  </validation-provider>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import { kebabCase } from 'lodash'
import vSelect from 'vue-select'
import 'vue-select/src/scss/vue-select.scss'

export default {
  name: 'base-select-input',

  components: { ValidationProvider, vSelect },

  props: {
    getOptionLabel: { Function },
    searchable: { Boolean },
    autofocus: { Boolean, default: false },
    immediate: { Boolean },
    disabled: { Boolean, default: false },
    options: { Array, default: () => [] },
    reduce: { Function },
    label: { String, required: true },
    value: { String },
    rules: { String },
    name: { String, required: true }
  },

  data: () => ({
    innerValue: null,
    components: {
      OpenIndicator: {
        render: createElement =>
          createElement('base-icon', {
            props: { name: 'arrow-down', size: '24px' }
          })
      }
    }
  }),

  computed: {
    hasContent() {
      return this.value && this.value.length > 0
    },

    idFromName() {
      return kebabCase(this.name)
    }
  },

  watch: {
    value(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.innerValue = newValue
      }
    },

    innerValue(val) {
      this.$emit('input', val)
    }
  },

  created() {
    // required to prefill with initial value
    this.innerValue = this.value
  }
}
</script>

<style lang="scss" module>
.container {
  margin-bottom: spacing(2);
  position: relative;

  &.disabled {
    opacity: 0.6;
  }

  :global(.vs__search) {
    color: $color-copy;
    font-family: $font-primary;
    font-size: $size-medium;
    font-weight: $weight-regular;
    line-height: 1.5;
    padding: spacing(3) spacing(2) spacing(1) spacing(2);
    border: 0;
    margin-top: 0;
    opacity: 0;
  }

  :global(.v-select ul) {
    list-style-type: none;
    padding-left: 0;
    margin-top: 0;
    position: absolute;
    width: 100%;
    z-index: 10;
  }

  :global(.vs--disabled .vs__search),
  :global(.vs--disabled .vs__open-indicator) {
    background-color: transparent;
    cursor: not-allowed;
  }

  :global(.vs__dropdown-toggle) {
    background-color: $color-fields;
    border-radius: 0;
    border: none;
    height: 56px;
    box-shadow: 0 -3px 0 -2px #c2c4c6 inset;
    padding-bottom: 0;
  }

  &.hasContent :global(.vs__dropdown-toggle) {
    background-color: rgba(0, 143, 202, 0.1);
  }

  &.valid :global(.vs__dropdown-toggle) {
    box-shadow: 0 -4px 0 -2px $color-success inset;
  }

  &.invalid :global(.vs__dropdown-toggle) {
    box-shadow: 0 -4px 0 -2px $color-danger inset;
  }

  :global(.vs--open .vs__dropdown-toggle) {
    background-color: rgba(0, 143, 202, 0.1);
    color: $color-labels;
  }

  :global(.vs__actions) {
    padding-right: spacing(1.5);
    position: absolute;
    right: 0;
    top: spacing(2.5);
  }

  :global(.vs__selected-options) {
    overflow: hidden;
    padding: 0;
  }

  :global(.vs__dropdown-menu) {
    background-color: $color-light;
    border-radius: $radius-small;
    margin-bottom: spacing(2);
    border: none;
    box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2),
      0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 8px 10px 1px rgba(0, 0, 0, 0.14);
  }

  :global(.vs__dropdown-option--highlight) {
    background-color: $color-primary-light;
    color: $color-light;
  }

  :global(.vs__dropdown-option) {
    padding: spacing(1) spacing(2);
  }

  :global(.vs__selected) {
    border: none;
    padding: spacing(3) 0 spacing(1) spacing(2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    margin: 0;
  }
}

.label {
  display: block;
  font-family: $font-primary;
  font-size: $size-medium;
  line-height: 1.5;
  position: absolute;
  left: spacing(2);
  transition: 0.2s;
  z-index: 1;
  pointer-events: none;

  // clean
  color: $color-labels;
  font-weight: $weight-regular;
  top: spacing(2);

  .container:focus-within &,
  .container.hasContent & {
    color: $color-primary-light;
    font-size: $size-xx-small;
    font-weight: $weight-medium;
    letter-spacing: 0.4px;
    top: spacing(1);
  }
}

.inputWrapper {
  position: relative;
}

input:disabled {
  cursor: not-allowed;
}

.inputDirty,
.inputClean {
  border: none;
  width: 100%;
  display: block;
  color: $color-copy;
  font-family: $font-primary;
  font-size: $size-medium;
  font-weight: $weight-regular;
  line-height: 1.5;
  outline: none;
  padding: spacing(3) spacing(6) spacing(1) spacing(2);
  box-shadow: 0 -3px 0 -2px #c2c4c6 inset;

  &:global(.is-invalid) {
    box-shadow: 0 -4px 0 -2px $color-danger inset;
  }

  &:global(.is-valid) {
    box-shadow: 0 -4px 0 -2px $color-success inset;
  }
}

.inputDirty,
.inputDirty:focus,
.inputClean:focus {
  background-color: rgba(0, 143, 202, 0.1);
}

.error {
  font-size: $size-xx-small;
  font-weight: $weight-medium;
  line-height: $size-medium;
  letter-spacing: 0.4px;
  color: $color-danger;
  min-height: spacing(2);
  margin-left: spacing(2);
  margin-top: 2px;
  text-align: left;
}
</style>
